import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { EMPTY_PROGRESS } from "../components/Help/pilotTrainingSteps";

/**
 * Per-user pilot-training progress (MF §14B, PILOT-TECHNICIAN-TRAINING-001).
 *
 * Persisted in localStorage under a per-user key. This is the technician's own
 * checklist progress — a display/onboarding preference, not an operational
 * record — so no backend write is involved and no authority is derived from it.
 * Marking a step done records that the technician read it; it never grants a
 * permission and is never consulted by any policy or approval path.
 *
 * Storage failures degrade to in-memory state, never to an error.
 *
 * `status` is the only interactive state stored. "Completed" is deliberately NOT
 * stored: it is derived from the steps the user can currently see, so a change
 * in permissions cannot leave a stale "you finished training" claim behind.
 */

const STORAGE_PREFIX = "prodigy.pilotTraining.v1:";
const VALID_STATUS = ["new", "active", "paused", "dismissed"];

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId}`;
}

export function loadTrainingState(userId) {
  if (!userId) return EMPTY_PROGRESS;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return EMPTY_PROGRESS;
    const parsed = JSON.parse(raw);
    return {
      status: VALID_STATUS.includes(parsed.status) ? parsed.status : "new",
      done:
        parsed.done && typeof parsed.done === "object" && !Array.isArray(parsed.done)
          ? parsed.done
          : {},
      demoDone: parsed.demoDone === true,
    };
  } catch {
    return EMPTY_PROGRESS;
  }
}

function saveTrainingState(userId, state) {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Storage unavailable/full: keep the in-memory state only.
  }
}

const PilotTrainingContext = createContext(null);

export function PilotTrainingProvider({ children }) {
  const { user } = useAuth() || {};
  const userId = user?.id ?? null;
  const [progress, setProgress] = useState(() => loadTrainingState(userId));
  const [open, setOpen] = useState(false);

  // Load the signed-in user's own progress; never inherit a previous user's.
  useEffect(() => {
    setProgress(loadTrainingState(userId));
    setOpen(false);
  }, [userId]);

  const update = useCallback(
    (fn) => {
      setProgress((prev) => {
        const next = fn(prev);
        saveTrainingState(userId, next);
        return next;
      });
    },
    [userId]
  );

  const value = useMemo(
    () => ({
      progress,
      status: progress.status,
      open,
      isStepDone: (stepId) => Boolean(progress.done[stepId]),
      markStep: (stepId, done) =>
        update((p) => ({ ...p, done: { ...p.done, [stepId]: done !== false } })),
      markDemoDone: () => update((p) => ({ ...p, demoDone: true })),

      // First-use prompt is offered exactly once, until the technician acts on
      // it in any way (start / pause / dismiss) or finishes every visible step.
      shouldOffer: progress.status === "new",

      start: () => {
        update((p) => ({ ...p, status: "active" }));
        setOpen(true);
      },
      // Relaunch (from Help) works from any status, including dismissed/paused.
      openTraining: () => {
        update((p) => (p.status === "new" ? { ...p, status: "active" } : p));
        setOpen(true);
      },
      closeTraining: () => setOpen(false),
      pause: () => {
        update((p) => ({ ...p, status: "paused" }));
        setOpen(false);
      },
      dismiss: () => {
        update((p) => ({ ...p, status: "dismissed" }));
        setOpen(false);
      },
      resume: () => {
        update((p) => ({ ...p, status: "active" }));
        setOpen(true);
      },
      // Restart clears progress but keeps the explicit "active" status, so the
      // first-use prompt does not reappear for someone who already saw it.
      restart: () => {
        update(() => ({ ...EMPTY_PROGRESS, status: "active" }));
        setOpen(true);
      },
    }),
    [progress, open, update]
  );

  return <PilotTrainingContext.Provider value={value}>{children}</PilotTrainingContext.Provider>;
}

export const usePilotTraining = () => useContext(PilotTrainingContext);
